class ToggleDiscount extends HTMLElement {
  #selectors = {
    checkbox: '[type="checkbox"]',
    discountTitle: '.toggle-discount__title',
    label: '.label',
  };

  connectedCallback() {
    this.checkbox = this.querySelector(this.#selectors.checkbox);

    this.defaultDiscount = {
      code: this.dataset.defaultCode,
      title: this.dataset.defaultTitle,
      value: this.dataset.defaultValue,
    };

    this.holidayDiscount = this.dataset.holidayCode ? {
      code: this.dataset.holidayCode,
      title: this.dataset.holidayTitle,
      value: this.dataset.holidayValue,
    } : null;

    this.originalData = {
      code: this.checkbox.name,
      discountTitle: this.querySelector(this.#selectors.discountTitle).textContent,
      label: this.querySelector(this.#selectors.label).textContent,
      appliesOnSubscription: this.checkbox.dataset.appliesOnSubscription,
      appliesOnOneTimePurchase: this.checkbox.dataset.appliesOnOneTimePurchase,
    };

    this.style.display = 'none';
    this.#initializeComponent();
  }

  async #initializeComponent() {
    this.#setupEventListeners();
    await this.#selectBestDiscount();
    this.#applyInitialDiscountState();
    this.#updateDisplayStyle();
  }

  async #selectBestDiscount() {
    const product = this.closest('main-product');
    if (!product?.product) return;

    if (this.holidayDiscount) {
      const isHolidayEligible = await Shopify.discount.checkDiscountEligibility(
        product.product,
        this.holidayDiscount.code,
        false
      );

      if (isHolidayEligible) {
        this.#setDiscount(this.holidayDiscount);
        return;
      }
    }

    this.#setDiscount(this.defaultDiscount);
  }

  #setDiscount(discount) {
    this.checkbox.name = discount.code;
    this.querySelector(this.#selectors.discountTitle).textContent = `${Math.floor(discount.value)}% coupon:`;
    this.querySelector(this.#selectors.label).textContent = discount.title;

    this.originalData = {
      code: discount.code,
      discountTitle: this.querySelector(this.#selectors.discountTitle).textContent,
      label: this.querySelector(this.#selectors.label).textContent,
      appliesOnSubscription: this.checkbox.dataset.appliesOnSubscription,
      appliesOnOneTimePurchase: this.checkbox.dataset.appliesOnOneTimePurchase,
    };
  }

  #setupEventListeners() {
    this.checkbox?.addEventListener('change', this.#handleDiscountChange);
    Shopify.discount.onUpdated(this.#handleDiscountUpdated.bind(this));
    document.addEventListener('countrySelector:ready', this.#updateDisplayStyle.bind(this));
    document.addEventListener('sellingPlan:update', async () => {
      await this.#selectBestDiscount();
      this.#applyInitialDiscountState();
      await this.#updateDisplayStyle();
    });
  }

  #applyInitialDiscountState() {
    const product = this.closest('main-product');
    const appliedDiscountCode = Shopify.discount.appliedDiscountCodes.find(code =>
      Shopify.discount.isProductEligibleForDiscount(product.product, code, !!product.sellingPlanId),
    );
    
    if (appliedDiscountCode) {
      const discount = Shopify.discount.getDiscountInfoByCode(appliedDiscountCode);
      this.checkbox.name = appliedDiscountCode;
      this.checkbox.dataset.appliesOnSubscription = discount.appliesOnSubscription;
      this.checkbox.dataset.appliesOnOneTimePurchase = discount.appliesOnOneTimePurchase;
      this.querySelector(this.#selectors.discountTitle).textContent = discount.value.value + '% coupon:';
      this.querySelector(this.#selectors.label).textContent = appliedDiscountCode;
    } else {
      this.checkbox.name = this.originalData.code;
      this.checkbox.dataset.appliesOnSubscription = this.originalData.appliesOnSubscription;
      this.checkbox.dataset.appliesOnOneTimePurchase = this.originalData.appliesOnOneTimePurchase;
      this.querySelector(this.#selectors.discountTitle).textContent = this.originalData.discountTitle;
      this.querySelector(this.#selectors.label).textContent = this.originalData.label;
    }

    this.checkbox.checked = !!appliedDiscountCode;
  }

  async #updateDisplayStyle() {
    const product = this.closest('main-product');
    if (!product?.product) {
      this.style.display = 'none';
      return;
    }
    
    const isEligibleCountry = !Shopify.currentCountry || ['US', 'CA'].includes(Shopify.currentCountry.code);
    
    const isEligiblePurchaseType = !!product.sellingPlanId
      ? this.checkbox.dataset.appliesOnSubscription === 'true'
      : this.checkbox.dataset.appliesOnOneTimePurchase === 'true';

    if (!isEligibleCountry || !isEligiblePurchaseType) {
      this.style.display = 'none';
      return;
    }

    const isEligibleForCurrentDiscount = await Shopify.discount.checkDiscountEligibility(
      product.product,
      this.checkbox.name,
      this.checkbox.dataset.appliesOnSubscription === 'true'
    );

    if (isEligibleForCurrentDiscount) {
      this.style.display = 'flex';
      return;
    }

    if (this.holidayDiscount && this.checkbox.name === this.holidayDiscount.code) {
      const isDefaultEligible = await Shopify.discount.checkDiscountEligibility(
        product.product,
        this.defaultDiscount.code,
        false
      );

      if (isDefaultEligible) {
        this.#setDiscount(this.defaultDiscount);
        this.style.display = 'flex';
        return;
      }
    }

    this.style.display = 'none';
  }

  #handleDiscountChange = event => {
    const code = event.target.name;
    const isChecked = event.target.checked;
    this.#loading(true);
    isChecked ? Shopify.discount.apply([code]) : Shopify.discount.clear([code]);
  };

  async #handleDiscountUpdated() {
    await this.#selectBestDiscount();
    this.#applyInitialDiscountState();
    await this.#updateDisplayStyle();
    this.#loading(false);
  }

  #loading(value) {
    if (value) {
      this.setAttribute('data-loading', '');
      this.checkbox.setAttribute('disabled', '');
    } else {
      this.checkbox.removeAttribute('disabled');
      this.removeAttribute('data-loading');
    }
  }
}

customElements.define('toggle-discount', ToggleDiscount);
